import { useBooleanState } from '@cian/react-utils';
import { Responsive } from '@cian/ui-kit';
import { Button } from '@cian/ui-kit/button';
import { IconActionSearch16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { disablePlaceholder } from 'shared/actions/placeholderDisabled';

import { DesktopSearchBlockLayout } from 'shared/components/DesktopSearchBlockLayout/DesktopSearchBlock';
import { MobileSearchBlock } from 'shared/components/MobileSearchBlock';
import { RedesignSearchBox } from 'shared/components/RedesignSearchBox';
import { RentDailySearchBox } from 'shared/components/RentDailySearchBox';
import { RedesignMainpageSearchBox } from 'shared/components/RedesignMainpageSearchBox';
import { getMainpageFiltersUrl } from 'shared/constants/constants';
import { selectPlaceholderDisabled } from 'shared/selectors/placeholderDisabled';
import { selectSearchBlockView } from 'shared/selectors/searchBlockView';
import { selectSearchQuery } from 'shared/selectors/searchQuery';
import { selectCurrentRegion } from 'shared/selectors/regions';
import { ESearchBlockView } from 'shared/types/searchBlockView';
import { dispatchFilterClickEvent } from 'shared/utils/filterClickEvent';
import { isRentDailyPageType, isSearchPageType } from 'shared/utils/pageType';

import { useApplicationContext } from '../ApplicationContext';
import { DesktopSearchInputContainer } from '../DesktopSearchInputContainer';
import { SearchBlockModalContainer } from '../SearchBlockModalContainer';
import { SearchTipsContainer } from '../SearchTipsContainer/SearchTipsContainer';
import { trackSearchStringClick } from './tracking';
import { TSearchStringPlaceholders } from 'shared/types/searchStringPlaceholders';
import { useMemo } from 'react';
import { EVertical } from 'shared/types/vertical';

export const SearchBlockContainer: React.FC = () => {
  const {
    custom: { isRedesignedMainPage, userLocationId, withPlaceholders, vertical, pageType },
    config,
  } = useApplicationContext();
  const dispatch = useDispatch();
  const currentRegionId = useSelector(selectCurrentRegion);
  const placeholderDisabled = useSelector(selectPlaceholderDisabled);
  const searchQuery = useSelector(selectSearchQuery);
  const searchBlockView = useSelector(selectSearchBlockView);

  const isRentDaily = isRentDailyPageType(pageType);

  // Выдачу пролистали — перебор плейсхолдеров больше не нужен.
  const isSearchPage = isSearchPageType(pageType);
  const handleSearchBoxCollapse = React.useCallback(() => {
    if (isSearchPage && !placeholderDisabled) {
      dispatch(disablePlaceholder());
    }
  }, [isSearchPage, placeholderDisabled, dispatch]);

  const { state: isModalOpen, setTrue: setModalOpen, setFalse: setModalClose } = useBooleanState(false);
  /**
   * Включает анимацию поисковой строки в обход системной настройки «уменьшить движение».
   * Нужна, чтобы анимацию можно было посмотреть на устройстве, где движение отключено на уровне ОС.
   */
  const isAnimationForced = Boolean(config.get<boolean>('BX.SearchStringAnimationForced.Enabled'));
  const placeholders = useMemo(() => {
    if (!withPlaceholders) {
      return null;
    }

    const placeholders = config.get<TSearchStringPlaceholders>('BX.SearchStringPlaceholders');

    if (!placeholders || !vertical || vertical === EVertical.Unexpected) {
      return null;
    }

    return placeholders[vertical];
  }, [config]);

  const filtersRegionId = currentRegionId || userLocationId;
  const mainpageFiltersUrl = getMainpageFiltersUrl(filtersRegionId);

  const handleSearchButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setModalOpen();
    },
    [setModalOpen],
  );

  const openRedesignSearchModal = React.useCallback(() => {
    trackSearchStringClick(false);
    setModalOpen();
  }, [setModalOpen]);

  const handleRedesignSearchButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      openRedesignSearchModal();
    },
    [openRedesignSearchModal],
  );

  const handleFiltersButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      trackSearchStringClick(true);

      // У посуточной выдачи своя логика кнопки фильтров, поэтому переходом занимается хост:
      // мы, как и на десктопе, только сообщаем ему о клике событием.
      if (isRentDaily) {
        event.preventDefault();
        dispatchFilterClickEvent();
      }
    },
    [isRentDaily],
  );

  const renderMobileSearchBlock = (): React.ReactNode => {
    if (searchBlockView === ESearchBlockView.Button) {
      return (
        <Button
          theme="main_secondary"
          size="XS"
          title="Найти"
          beforeIcon={<IconActionSearch16 color="icon-main-default" />}
          onClick={openRedesignSearchModal}
          data-testid="SearchBlockButton"
        />
      );
    }

    if (placeholders && isRentDaily) {
      return (
        <RentDailySearchBox
          filtersUrl={mainpageFiltersUrl}
          onSearchClick={handleRedesignSearchButtonClick}
          onFiltersClick={handleFiltersButtonClick}
          placeholders={placeholders}
          disablePlaceholder={placeholderDisabled}
          searchQuery={searchQuery || undefined}
        />
      );
    }

    if (placeholders) {
      return (
        <RedesignSearchBox
          filtersUrl={mainpageFiltersUrl}
          onSearchClick={handleRedesignSearchButtonClick}
          onFiltersClick={handleFiltersButtonClick}
          placeholders={placeholders}
          disablePlaceholder={placeholderDisabled}
          searchQuery={searchQuery || undefined}
          onCollapse={handleSearchBoxCollapse}
          isAnimationForced={isAnimationForced}
        />
      );
    }

    if (isRedesignedMainPage) {
      return (
        <RedesignMainpageSearchBox
          filtersUrl={mainpageFiltersUrl}
          onSearchClick={handleRedesignSearchButtonClick}
          onFiltersClick={handleFiltersButtonClick}
        />
      );
    }

    return <MobileSearchBlock onSearchClick={handleSearchButtonClick} isMainpage />;
  };

  return (
    <>
      <Responsive desktop>
        <DesktopSearchBlockLayout>
          <DesktopSearchInputContainer />
          <SearchTipsContainer isMainPageTips={true} desktopStyle={true} onClick={() => {}} />
        </DesktopSearchBlockLayout>
      </Responsive>
      <Responsive phone tablet>
        {renderMobileSearchBlock()}
        <SearchBlockModalContainer open={isModalOpen} onClose={setModalClose} />
      </Responsive>
    </>
  );
};

SearchBlockContainer.displayName = 'SearchBlockContainer';
